import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  TableChart,
  PictureAsPdf,
  Refresh,
  Edit,
  CheckCircle,
  HelpOutline,
  Visibility,
  Close,
  OpenInNew
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { getSalaryReport, getOffboardedSalaryReport } from '../services/reportingAPIs';
import { getViolationsOverview, getEmployeeNotes } from '../services/NoteAPIs';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axopeopleLogo from '../assets/images/logos/Axopeople.png';
import { getAllInactiveEmployees } from '../services/EmployeeService';

// Theme matching existing premium layouts
const THEME = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  primary: '#1E293B',
  secondary: '#64748B',
  accent: '#3B82F6',
  border: '#E2E8F0',
  sageLight: '#E2EFDA',
  sageDark: '#385723',
  addLight: '#E2EFDA', // light green
  deductLight: '#FCE4D6' // light soft orange/pink
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: '20px',
    backgroundColor: THEME.bg,
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  headerCard: {
    backgroundColor: THEME.surface,
    padding: '24px',
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid ${THEME.border}`,
    flexWrap: 'wrap',
    gap: '16px'
  },
  titleText: {
    fontSize: '1.5rem',
    fontWeight: '800',
    color: THEME.primary,
    letterSpacing: '-0.025em'
  },
  subTitle: {
    fontSize: '0.875rem',
    color: THEME.secondary,
    marginTop: '4px'
  },
  exportBtn: {
    borderColor: THEME.border,
    color: THEME.secondary,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '8px 16px',
    borderRadius: '8px',
    '&:hover': {
      borderColor: THEME.accent,
      color: THEME.accent,
      backgroundColor: '#EFF6FF'
    }
  },
  tableCard: {
    backgroundColor: THEME.surface,
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    overflow: 'hidden'
  },
  th: {
    backgroundColor: '#F1F5F9',
    color: THEME.primary,
    fontWeight: '700',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    padding: '12px 16px',
    borderBottom: `2px solid ${THEME.border}`,
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },
  excelTh: {
    backgroundColor: THEME.sageLight,
    color: THEME.sageDark,
    fontWeight: '700',
    fontSize: '0.85rem',
    padding: '14px 16px',
    border: '1px solid #C6D9F1',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },
  td: {
    padding: '8px 12px',
    color: '#334155',
    fontSize: '0.875rem',
    borderBottom: `1px solid ${THEME.border}`,
    textAlign: 'center',
    whiteSpace: 'nowrap'
  },
  editableInput: {
    width: '75px',
    '& .MuiOutlinedInput-input': {
      padding: '4px 8px',
      fontSize: '0.85rem',
      textAlign: 'center'
    }
  }
};

const SalaryReport = () => {
  const today = new Date();
  const [startDate, setStartDate] = useState(startOfMonth(today));
  const [endDate, setEndDate] = useState(endOfMonth(today));
  const [activeTab, setActiveTab] = useState(0);
  const [nadorData, setNadorData] = useState([]);
  const [tangierData, setTangierData] = useState([]);
  const [consultingData, setConsultingData] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [offboardingData, setOffboardingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [previewLink, setPreviewLink] = useState(null);

  const selectedMonthLabel = format(startDate, 'MMMM');
  const selectedYear = format(startDate, 'yyyy');

  // Helper to format YYYY-MM-DD from API to DD/MM/YYYY
  const formatAPIDate = (dStr) => {
    if (!dStr) return '';
    const parts = dStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dStr;
  };

  const fetchSalaryReportData = async () => {
    setLoading(true);
    try {
      const fromStr = format(startDate, 'yyyy-MM-dd');
      const toStr = format(endDate, 'yyyy-MM-dd');

      const [salaryData, offboardedData, inactiveEmployees, violationsData] = await Promise.all([
        getSalaryReport(fromStr, toStr).catch(err => {
          console.warn("Failed to fetch salary report from API:", err);
          return [];
        }),
        getOffboardedSalaryReport(fromStr, toStr).catch(err => {
          console.warn("Failed to fetch offboarded salary report from API:", err);
          return [];
        }),
        getAllInactiveEmployees().catch(err => {
          console.warn("Failed to fetch inactive employees:", err);
          return [];
        }),
        getViolationsOverview().catch(err => {
          console.warn("Failed to fetch violations overview:", err);
          return { employeeNotes: [] };
        })
      ]);

      const inactiveIds = new Set(inactiveEmployees.map(emp => emp.employeeId || emp.id).filter(Boolean));

      const nador = [];
      const tangier = [];
      const consulting = [];
      const teamMay = [];
      const offboard = [];

      let allNotes = Array.isArray(violationsData) ? violationsData : (violationsData?.employeeNotes || []);

      if (salaryData && salaryData.length > 0) {
        // Fetch specific notes for new onboarded employees (diffDays <= 45)
        const newOnboardedIds = salaryData.filter(item => {
          if (!item.startDate) return false;
          const empStart = new Date(item.startDate);
          const diffDays = Math.ceil(Math.abs(endDate - empStart) / (1000 * 60 * 60 * 24));
          return diffDays <= 45;
        }).map(item => item.employeeId || item.id);

        if (newOnboardedIds.length > 0) {
          const teamNotesPromises = newOnboardedIds.map(id => getEmployeeNotes(id).catch(() => []));
          const teamNotesResults = await Promise.all(teamNotesPromises);
          teamNotesResults.forEach(notes => {
             if (Array.isArray(notes)) {
                allNotes = [...allNotes, ...notes];
             }
          });
        }

        dataDataProcess(salaryData, nador, tangier, consulting, teamMay, offboard, inactiveIds, allNotes);
      }

      // Sort each group by start date: oldest → newest
      const parseDMY = (dateStr) => {
        if (!dateStr) return new Date(0);
        const parts = dateStr.split('/');
        if (parts.length === 3) return new Date(parts[2], parts[1] - 1, parts[0]);
        return new Date(dateStr);
      };
      const sortByStartDate = (arr) =>
        [...arr].sort((a, b) => parseDMY(a.startDate) - parseDMY(b.startDate));

      setNadorData(sortByStartDate(nador));
      setTangierData(sortByStartDate(tangier));
      setConsultingData(sortByStartDate(consulting));
      setTeamData(sortByStartDate(teamMay));

      if (offboardedData && offboardedData.length > 0) {
        const parsedOffboarding = offboardedData.map(item => {
          const [firstName = '', ...lastNameParts] = item.fullName ? item.fullName.split(' ') : ['', ''];
          const lastName = lastNameParts.join(' ');
          return {
            id: item.employeeId,
            firstName,
            lastName,
            entryDate: formatAPIDate(item.startDate),
            exitDate: formatAPIDate(item.exitDate),
            salary: item.totalFinalPay ? `${Number(item.totalFinalPay).toLocaleString()} MAD` : '0 MAD'
          };
        });
        setOffboardingData(parsedOffboarding);
      } else {
        setOffboardingData(offboard);
      }
    } catch (error) {
      console.warn("Failed to fetch salary report from API:", error);
      setNadorData([]);
      setTangierData([]);
      setConsultingData([]);
      setTeamData([]);
      setOffboardingData([]);
    } finally {
      setLoading(false);
    }
  };

  const dataDataProcess = (data, nador, tangier, consulting, teamMay, offboard, inactiveIds, violationsData) => {
    const allNotes = Array.isArray(violationsData) ? violationsData : (violationsData?.employeeNotes || []);

    data.forEach(item => {
      const [firstName = '', ...lastNameParts] = item.employeeName ? item.employeeName.split(' ') : ['', ''];
      const lastName = lastNameParts.join(' ');
      const startDateFormatted = formatAPIDate(item.startDate);

      const empId = item.employeeId || item.id;
      const empNotes = allNotes.filter(n => 
        String(n.employeeId) === String(empId) || 
        String(n.employee?.id) === String(empId) || 
        String(n.employee?.employeeId) === String(empId)
      );
      
      const arrivalNote = empNotes.find(n => {
        const vType = (n.violationType || '').toLowerCase().trim();
        const nType = (n.type || '').toLowerCase().trim();
        return vType === 'new arrivals feedback' || 
               vType === 'new arrival feedback' || 
               nType === 'new arrivals feedback' || 
               nType === 'new arrival feedback';
      });
      
      const newArrivalFeedback = arrivalNote ? (arrivalNote.description || arrivalNote.violationDescription || arrivalNote.managerReview || 'Feedback Present') : '';
      const newArrivalFeedbackType = arrivalNote ? (arrivalNote.noteType || 'NEGATIVE') : null;

      const mapped = {
        id: item.employeeId,
        firstName,
        lastName,
        startDate: startDateFormatted,
        commitments: item.totalPersonalHours || 0,
        sickness: item.totalSicknessHours || 0,
        unpaid: item.totalUnpaidVacationHours || 0,
        extraWeek: item.overtimeHoursDuringTheWeek || 0,
        extraWeekend: item.overtimeHoursDuringTheWeekend || 0,
        extraWorked: (item.overtimeHoursDuringTheWeek || 0) + (item.overtimeHoursDuringTheWeekend || 0),
        links: item.justifications && item.justifications.length > 0 ? item.justifications.join(', ') : 'N/A',
        comment: newArrivalFeedback,
        commentType: newArrivalFeedbackType,
        company: item.company,
        office: item.office
      };

      const companyLower = (item.company || '').toLowerCase().trim();
      const officeLower = (item.office || '').toLowerCase().trim();
      const positionLower = (item.position || item.positionName || '').toLowerCase().trim();
      const departmentLower = (item.department || item.departmentName || item.department?.name || '').toLowerCase().trim();
      const isInactive = 
        item.active === false || 
        (item.status || '').toUpperCase() === 'INACTIVE' || 
        (item.employeeStatus || '').toUpperCase() === 'INACTIVE' ||
        (inactiveIds && (inactiveIds.has(item.employeeId) || inactiveIds.has(item.id)));
      const isFacility = departmentLower === 'facility' || positionLower === 'facility' || officeLower === 'facility' || companyLower === 'facility';

      let isNewOnboarded = false;
      if (item.startDate) {
        const empStart = new Date(item.startDate);
        if (!isNaN(empStart.getTime())) {
          isNewOnboarded = empStart.getFullYear() === startDate.getFullYear() && empStart.getMonth() === startDate.getMonth();
        }
      }

      if (companyLower.includes('offboard')) {
        offboard.push({
          id: item.employeeId,
          firstName,
          lastName,
          entryDate: startDateFormatted,
          exitDate: item.exitDate ? formatAPIDate(item.exitDate) : '31/05/2026',
          salary: item.salary || (item.totalFinalPay ? `${Number(item.totalFinalPay).toLocaleString()} MAD` : '8,500 MAD')
        });
      } else if (!isInactive && !isFacility) {
        let addedToTeam = false;

        if (companyLower === 'mc' || companyLower.includes('consulting')) {
          consulting.push(mapped);
        } else if (companyLower.includes('team') || companyLower.includes('may')) {
          teamMay.push(mapped);
          addedToTeam = true;
        } else if (companyLower === 'mg' || companyLower.includes('group')) {
          if (officeLower.includes('tangier') || officeLower.includes('tanger')) {
            tangier.push(mapped);
          } else {
            nador.push(mapped);
          }
        } else {
          if (officeLower.includes('tangier') || officeLower.includes('tanger')) {
            tangier.push(mapped);
          } else {
            nador.push(mapped);
          }
        }

        if (isNewOnboarded && !addedToTeam) {
          teamMay.push(mapped);
        }
      }
    });
  };

  useEffect(() => {
    fetchSalaryReportData();
  }, [startDate, endDate]);

  // Cell change handlers
  const handleNumericChange = (listType, id, field, val) => {
    const numericVal = val === '' ? 0 : Number(val);
    if (isNaN(numericVal)) return;

    const updateList = (prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: numericVal } : item));

    if (listType === 'nador') setNadorData(updateList);
    if (listType === 'tangier') setTangierData(updateList);
    if (listType === 'consulting') setConsultingData(updateList);
    if (listType === 'team') setTeamData(updateList);
  };

  // Excel Multi-sheet Export — matches exact structure of reference salary report
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    const thinBorder = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    };

    const headerStyle = {
      font: { name: 'Calibri', sz: 11, bold: true },
      fill: { fgColor: { rgb: 'C6E0B4' } }, // Premium light green sage
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: thinBorder
    };

    const titleStyle = {
      font: { name: 'Calibri', sz: 14, bold: true },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: thinBorder
    };

    const dataFont = { name: 'Calibri', sz: 11, color: { rgb: '000000' } };
    const dataStyle = { font: dataFont, border: thinBorder };
    const dataCenterStyle = { font: dataFont, border: thinBorder, alignment: { horizontal: 'center' } };
    const numStyle = { font: { name: 'Calibri', sz: 11 }, border: thinBorder, alignment: { horizontal: 'center' } };
    const boldNumStyle = { font: { name: 'Calibri', sz: 11, bold: true }, border: thinBorder, alignment: { horizontal: 'center' } };
    const dateStyle = { font: dataFont, border: thinBorder, alignment: { horizontal: 'center' }, numFmt: 'dd/mm/yyyy' };

    // ===== Sheet 1: MARABES Group (Nador + Tangier) =====
    const ws2 = {};

    // 1. Nador Section Title
    ws2['A2'] = { v: `Report ${selectedMonthLabel} - MARABES Group (Nador)`, s: titleStyle };

    const s2Headers = [
      'Employee Name', '', 'Start Date',
      'Personnel Commitments (Deduct)', 'Sickness / Certificate (Deduct)',
      'Unpaid Vacation (Deduct)', 'Total Hours deducted',
      'Extra Hours during the week (Add)', 'Extra Hours during the weekend (Add)',
      'Hours to be paid or deducted', 'Links'
    ];
    const s2ColKeys = ['A','B','C','D','E','F','G','H','I','J','K'];

    // 2. Nador Headers at row 4
    s2Headers.forEach((h, i) => {
      ws2[s2ColKeys[i] + '4'] = { v: h, s: headerStyle };
    });

    let currentRow = 5;
    
    // Nador Data Rows
    nadorData.forEach((emp) => {
      const r = currentRow;
      ws2['A' + r] = { v: emp.firstName || '', s: dataStyle };
      ws2['B' + r] = { v: emp.lastName || '', s: dataStyle };
      if (emp.startDate) {
        const parts = emp.startDate.split('/');
        ws2['C' + r] = { v: new Date(parts[2], parts[1] - 1, parts[0]), t: 'd', s: dateStyle };
      } else {
        ws2['C' + r] = { v: '', s: dataStyle };
      }
      ws2['D' + r] = { v: emp.commitments || 0, s: numStyle };
      ws2['E' + r] = { v: emp.sickness || 0, s: numStyle };
      ws2['F' + r] = { v: emp.unpaid || 0, s: numStyle };
      ws2['G' + r] = { f: `D${r}+E${r}+F${r}`, s: numStyle };
      ws2['H' + r] = { v: emp.extraWeek || 0, s: numStyle };
      ws2['I' + r] = { v: emp.extraWeekend || 0, s: numStyle };
      const hasLink = emp.links && emp.links !== 'N/A';
      ws2['K' + r] = {
        v: hasLink ? 'View' : '-',
        l: hasLink ? { Target: emp.links.split(',')[0].trim() } : undefined,
        s: hasLink 
          ? { font: { name: 'Calibri', sz: 11, color: { rgb: '0563C1' }, underline: true }, border: thinBorder, alignment: { horizontal: 'center' } } 
          : { font: dataFont, border: thinBorder, alignment: { horizontal: 'center' } }
      };
      currentRow++;
    });

    // Spacer
    currentRow += 2;

    // 3. Tangier Section Title
    const tangierTitleRow = currentRow;
    ws2['A' + tangierTitleRow] = { v: `Report ${selectedMonthLabel} - MARABES Group (Tangier)`, s: titleStyle };
    currentRow += 2;

    // 4. Tangier Headers
    const tangierHeaderRow = currentRow;
    s2Headers.forEach((h, i) => {
      ws2[s2ColKeys[i] + tangierHeaderRow] = { v: h, s: headerStyle };
    });
    currentRow++;

    // Tangier Data Rows
    tangierData.forEach((emp) => {
      const r = currentRow;
      ws2['A' + r] = { v: emp.firstName || '', s: dataStyle };
      ws2['B' + r] = { v: emp.lastName || '', s: dataStyle };
      if (emp.startDate) {
        const parts = emp.startDate.split('/');
        ws2['C' + r] = { v: new Date(parts[2], parts[1] - 1, parts[0]), t: 'd', s: dateStyle };
      } else {
        ws2['C' + r] = { v: '', s: dataStyle };
      }
      ws2['D' + r] = { v: emp.commitments || 0, s: numStyle };
      ws2['E' + r] = { v: emp.sickness || 0, s: numStyle };
      ws2['F' + r] = { v: emp.unpaid || 0, s: numStyle };
      ws2['G' + r] = { f: `D${r}+E${r}+F${r}`, s: numStyle };
      ws2['H' + r] = { v: emp.extraWeek || 0, s: numStyle };
      ws2['I' + r] = { v: emp.extraWeekend || 0, s: numStyle };
      const hasLinkT = emp.links && emp.links !== 'N/A';
      ws2['K' + r] = {
        v: hasLinkT ? 'View' : '-',
        l: hasLinkT ? { Target: emp.links.split(',')[0].trim() } : undefined,
        s: hasLinkT 
          ? { font: { name: 'Calibri', sz: 11, color: { rgb: '0563C1' }, underline: true }, border: thinBorder, alignment: { horizontal: 'center' } } 
          : { font: dataFont, border: thinBorder, alignment: { horizontal: 'center' } }
      };
      currentRow++;
    });

    const s2LastRow = currentRow - 1;
    ws2['!ref'] = `A2:K${s2LastRow}`;
    
    ws2['!merges'] = [
      { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } }, // Nador Title merge
      { s: { r: 3, c: 0 }, e: { r: 3, c: 1 } }, // Nador employee name header merge
      { s: { r: tangierTitleRow - 1, c: 0 }, e: { r: tangierTitleRow - 1, c: 10 } }, // Tangier Title merge
      { s: { r: tangierHeaderRow - 1, c: 0 }, e: { r: tangierHeaderRow - 1, c: 1 } } // Tangier employee name header merge
    ];

    ws2['!cols'] = [
      { wch: 18 }, { wch: 18 }, { wch: 13 },
      { wch: 20 }, { wch: 20 }, { wch: 16 },
      { wch: 15 }, { wch: 20 }, { wch: 20 },
      { wch: 18 }, { wch: 10 }
    ];

    // ===== Sheet 2: MARABES Consulting =====
    const ws3 = {};
    ws3['A2'] = { v: `Report ${selectedMonthLabel} - MARABES Consulting`, s: titleStyle };

    s2Headers.forEach((h, i) => {
      ws3[s2ColKeys[i] + '4'] = { v: h, s: headerStyle };
    });

    let ws3Row = 5;
    consultingData.forEach((emp) => {
      const r = ws3Row;
      ws3['A' + r] = { v: emp.firstName || '', s: dataStyle };
      ws3['B' + r] = { v: emp.lastName || '', s: dataStyle };
      if (emp.startDate) {
        const parts = emp.startDate.split('/');
        ws3['C' + r] = { v: new Date(parts[2], parts[1] - 1, parts[0]), t: 'd', s: dateStyle };
      } else {
        ws3['C' + r] = { v: '', s: dataStyle };
      }
      ws3['D' + r] = { v: emp.commitments || 0, s: numStyle };
      ws3['E' + r] = { v: emp.sickness || 0, s: numStyle };
      ws3['F' + r] = { v: emp.unpaid || 0, s: numStyle };
      ws3['G' + r] = { f: `D${r}+E${r}+F${r}`, s: numStyle };
      ws3['H' + r] = { v: emp.extraWeek || 0, s: numStyle };
      ws3['I' + r] = { v: emp.extraWeekend || 0, s: numStyle };
      const hasLinkC = emp.links && emp.links !== 'N/A';
      ws3['K' + r] = {
        v: hasLinkC ? 'View' : '-',
        l: hasLinkC ? { Target: emp.links.split(',')[0].trim() } : undefined,
        s: hasLinkC 
          ? { font: { name: 'Calibri', sz: 11, color: { rgb: '0563C1' }, underline: true }, border: thinBorder, alignment: { horizontal: 'center' } } 
          : { font: dataFont, border: thinBorder, alignment: { horizontal: 'center' } }
      };
      ws3Row++;
    });

    const s3LastRow = ws3Row - 1;
    ws3['!ref'] = `A2:K${s3LastRow}`;
    ws3['!merges'] = [
      { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } }, // Title merge
      { s: { r: 3, c: 0 }, e: { r: 3, c: 1 } }  // Employee name header merge
    ];
    ws3['!cols'] = ws2['!cols'];

    // ===== Sheet 3: Team =====
    const ws4 = {};
    ws4['A2'] = { v: `Team ${selectedMonthLabel}`, s: titleStyle };

    const s4Headers = [
      'Employee Name', '', 'Start Date',
      'Personnel Commitments (Deduct)', 'Sickness / Certificate (Deduct)',
      'Unpaid Vacation (Deduct)', 'Total Hours deducted',
      'Extra Hours Worked (Add)', 'Hours to be paid or deducted',
      'New Arrival Feedback'
    ];
    const s4ColKeys = ['A','B','C','D','E','F','G','H','I','J'];

    s4Headers.forEach((h, i) => {
      ws4[s4ColKeys[i] + '4'] = { v: h, s: headerStyle };
    });

    let ws4Row = 5;
    teamData.forEach((emp) => {
      const r = ws4Row;
      ws4['A' + r] = { v: emp.firstName || '', s: dataStyle };
      ws4['B' + r] = { v: emp.lastName || '', s: dataStyle };
      if (emp.startDate) {
        const parts = emp.startDate.split('/');
        ws4['C' + r] = { v: new Date(parts[2], parts[1] - 1, parts[0]), t: 'd', s: dateStyle };
      } else {
        ws4['C' + r] = { v: '', s: dataStyle };
      }
      ws4['D' + r] = { v: emp.commitments || 0, s: numStyle };
      ws4['E' + r] = { v: emp.sickness || 0, s: numStyle };
      ws4['F' + r] = { v: emp.unpaid || 0, s: numStyle };
      ws4['G' + r] = { f: `D${r}+E${r}+F${r}`, s: numStyle };
      ws4['H' + r] = { v: emp.extraWorked || 0, s: numStyle };
      ws4['I' + r] = { f: `H${r}-G${r}`, s: boldNumStyle };
      ws4['J' + r] = { v: emp.comment || '', s: dataStyle };
      ws4Row++;
    });

    const s4LastRow = ws4Row - 1;
    ws4['!ref'] = `A2:J${s4LastRow}`;
    ws4['!merges'] = [
      { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } }, // Title merge
      { s: { r: 3, c: 0 }, e: { r: 3, c: 1 } }  // Employee name header merge
    ];
    ws4['!cols'] = [
      { wch: 18 }, { wch: 18 }, { wch: 13 },
      { wch: 20 }, { wch: 20 }, { wch: 16 },
      { wch: 15 }, { wch: 20 }, { wch: 18 },
      { wch: 25 }
    ];

    // ===== Sheet 4: Offboarding =====
    const ws5 = {};
    ws5['A2'] = { v: 'OFFBOARDING', s: titleStyle };

    const offHeaders = ['Names', 'Entry Date', 'Exit Day', 'Period worked (November)', 'Period worked (In Hours)', 'Salary'];
    const offColKeys = ['A','B','C','D','E','F'];
    offHeaders.forEach((h, i) => {
      ws5[offColKeys[i] + '4'] = { v: h, s: headerStyle };
    });

    let ws5Row = 5;
    offboardingData.forEach((emp) => {
      const r = ws5Row;
      ws5['A' + r] = { v: `${emp.firstName} ${emp.lastName}`, s: dataStyle };
      ws5['B' + r] = { v: emp.entryDate || '', s: dataCenterStyle };
      ws5['C' + r] = { v: emp.exitDate || '', s: dataCenterStyle };
      ws5['D' + r] = { v: '', s: dataStyle };
      ws5['E' + r] = { v: '', s: numStyle };
      ws5['F' + r] = { v: emp.salary || '', s: dataCenterStyle };
      ws5Row++;
    });

    const s5LastRow = ws5Row - 1;
    ws5['!ref'] = `A2:F${s5LastRow}`;
    ws5['!merges'] = [
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } } // Title merge
    ];
    ws5['!cols'] = [
      { wch: 22 }, { wch: 14 }, { wch: 14 },
      { wch: 22 }, { wch: 20 }, { wch: 16 }
    ];

    XLSX.utils.book_append_sheet(wb, ws2, `${selectedMonthLabel} - MARABES Group`);
    XLSX.utils.book_append_sheet(wb, ws3, `${selectedMonthLabel}- MARABES Consulting`);
    XLSX.utils.book_append_sheet(wb, ws4, `Team ${selectedMonthLabel}`);
    XLSX.utils.book_append_sheet(wb, ws5, 'Offboarding');

    XLSX.writeFile(wb, `Salary_Report_${selectedMonthLabel}_${selectedYear}.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    const todayStr = format(new Date(), 'dd-MM-yyyy');
    const margin = 12;

    const addLogoAndHeader = (title) => {
      try {
        doc.addImage(axopeopleLogo, 'PNG', margin, 8, 28, 9);
      } catch (e) {
        console.warn("Logo not loaded");
      }
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(title, doc.internal.pageSize.width / 2, 14, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${todayStr}`, doc.internal.pageSize.width - margin - 35, 14);
    };

    // --- Page 1: MARABES Group Nador ---
    addLogoAndHeader(`Report ${selectedMonthLabel} - MARABES Group (Nador)`);
    const groupHeaders = [
      ['First Name', 'Last Name', 'Start Date', 'Commitments', 'Sickness', 'Unpaid', 'Total Deduct', 'Extra Week', 'Extra Weekend', 'Hours Paid', 'Links']
    ];
    const nadorRows = nadorData.map((r) => {
      const totDeduct = r.commitments + r.sickness + r.unpaid;
      const hoursPaid = (r.extraWeek + r.extraWeekend) - totDeduct;
      const linksVal = r.links && r.links !== 'N/A' ? 'View' : '-';
      return [r.firstName, r.lastName, r.startDate, r.commitments, r.sickness, r.unpaid, totDeduct, r.extraWeek, r.extraWeekend, hoursPaid, linksVal];
    });

    autoTable(doc, {
      startY: 22,
      head: groupHeaders,
      body: nadorRows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      columnStyles: { 10: { cellWidth: 15 } },
      headStyles: { fillColor: [56, 87, 35], textColor: [255, 255, 255] }, // dark green sage
      didParseCell: (data) => {
        if (data.section === 'body') {
          if (data.column.index >= 3 && data.column.index <= 5) {
            data.cell.styles.fillColor = [252, 228, 214]; // pink deduct light
          } else if (data.column.index >= 7 && data.column.index <= 8) {
            data.cell.styles.fillColor = [226, 239, 218]; // green add light
          }
          if (data.column.index === 10 && data.cell.text[0] === 'View') {
            data.cell.text = [''];
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 10 && data.cell.raw === 'View') {
          const cx = data.cell.x + data.cell.width / 2;
          const cy = data.cell.y + data.cell.height / 2;
          doc.setDrawColor(34, 197, 94); // Green
          doc.setLineWidth(0.8);
          doc.line(cx - 2, cy, cx - 0.5, cy + 1.5);
          doc.line(cx - 0.5, cy + 1.5, cx + 2, cy - 1.5);
        }
      }
    });

    // --- Page 2: MARABES Group Tangier ---
    doc.addPage();
    addLogoAndHeader(`Report ${selectedMonthLabel} - MARABES Group (Tangier)`);
    const tangierRows = tangierData.map((r) => {
      const totDeduct = r.commitments + r.sickness + r.unpaid;
      const hoursPaid = (r.extraWeek + r.extraWeekend) - totDeduct;
      const linksVal = r.links && r.links !== 'N/A' ? 'View' : '-';
      return [r.firstName, r.lastName, r.startDate, r.commitments, r.sickness, r.unpaid, totDeduct, r.extraWeek, r.extraWeekend, hoursPaid, linksVal];
    });

    autoTable(doc, {
      startY: 22,
      head: groupHeaders,
      body: tangierRows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      columnStyles: { 10: { cellWidth: 15 } },
      headStyles: { fillColor: [56, 87, 35], textColor: [255, 255, 255] },
      didParseCell: (data) => {
        if (data.section === 'body') {
          if (data.column.index >= 3 && data.column.index <= 5) {
            data.cell.styles.fillColor = [252, 228, 214];
          } else if (data.column.index >= 7 && data.column.index <= 8) {
            data.cell.styles.fillColor = [226, 239, 218];
          }
          if (data.column.index === 10 && data.cell.text[0] === 'View') {
            data.cell.text = [''];
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 10 && data.cell.raw === 'View') {
          const cx = data.cell.x + data.cell.width / 2;
          const cy = data.cell.y + data.cell.height / 2;
          doc.setDrawColor(34, 197, 94); // Green
          doc.setLineWidth(0.8);
          doc.line(cx - 2, cy, cx - 0.5, cy + 1.5);
          doc.line(cx - 0.5, cy + 1.5, cx + 2, cy - 1.5);
        }
      }
    });

    // --- Page 3: MARABES Consulting ---
    doc.addPage();
    addLogoAndHeader(`Report ${selectedMonthLabel} - MARABES Consulting`);
    const consultingRows = consultingData.map((r) => {
      const totDeduct = r.commitments + r.sickness + r.unpaid;
      const hoursPaid = (r.extraWeek + r.extraWeekend) - totDeduct;
      const linksVal = r.links && r.links !== 'N/A' ? 'View' : '-';
      return [r.firstName, r.lastName, r.startDate, r.commitments, r.sickness, r.unpaid, totDeduct, r.extraWeek, r.extraWeekend, hoursPaid, linksVal];
    });

    autoTable(doc, {
      startY: 22,
      head: groupHeaders,
      body: consultingRows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      columnStyles: { 10: { cellWidth: 15 } },
      headStyles: { fillColor: [56, 87, 35], textColor: [255, 255, 255] },
      didParseCell: (data) => {
        if (data.section === 'body') {
          if (data.column.index >= 3 && data.column.index <= 5) {
            data.cell.styles.fillColor = [252, 228, 214];
          } else if (data.column.index >= 7 && data.column.index <= 8) {
            data.cell.styles.fillColor = [226, 239, 218];
          }
          if (data.column.index === 10 && data.cell.text[0] === 'View') {
            data.cell.text = [''];
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 10 && data.cell.raw === 'View') {
          const cx = data.cell.x + data.cell.width / 2;
          const cy = data.cell.y + data.cell.height / 2;
          doc.setDrawColor(34, 197, 94); // Green
          doc.setLineWidth(0.8);
          doc.line(cx - 2, cy, cx - 0.5, cy + 1.5);
          doc.line(cx - 0.5, cy + 1.5, cx + 2, cy - 1.5);
        }
      }
    });

    // --- Page 4: Team May ---
    doc.addPage();
    addLogoAndHeader(`Team ${selectedMonthLabel} Report`);
    const teamHeaders = [
      ['First Name', 'Last Name', 'Start Date', 'Commitments', 'Sickness', 'Unpaid', 'Total Deduct', 'Extra Worked', 'Hours Paid', 'New Arrival Feedback']
    ];
    const teamRows = teamData.map((r) => {
      const totDeduct = r.commitments + r.sickness + r.unpaid;
      return [r.firstName, r.lastName, r.startDate, r.commitments, r.sickness, r.unpaid, totDeduct, r.extraWorked, r.extraWorked - totDeduct, r.comment];
    });

    autoTable(doc, {
      startY: 22,
      head: teamHeaders,
      body: teamRows,
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 2, halign: 'center' },
      headStyles: { fillColor: [56, 87, 35], textColor: [255, 255, 255] }
    });

    // --- Page 5: Offboarding ---
    doc.addPage();
    addLogoAndHeader('Offboarding Details');
    const offboardHeaders = [
      ['First Name', 'Last Name', 'Entry Date', 'Exit Date', 'Salary']
    ];
    const offboardRows = offboardingData.map((r) => [r.firstName, r.lastName, r.entryDate, r.exitDate, r.salary]);

    autoTable(doc, {
      startY: 22,
      head: offboardHeaders,
      body: offboardRows,
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 3, halign: 'center' },
      headStyles: { fillColor: [153, 27, 27], textColor: [255, 255, 255] }
    });

    doc.save(`Salary_Report_${selectedMonthLabel}_${selectedYear}.pdf`);
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerCard}>
        <Box>
          <Typography sx={styles.titleText}>Monthly Salary Report</Typography>
          <Typography sx={styles.subTitle}>
            Review and adjust monthly hours deductions, extra additions, and offboarding logs
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(v) => {
                if (v) setStartDate(v);
              }}
              format="dd/MM/yyyy"
              slotProps={{ textField: { size: 'small', sx: { width: 160, bgcolor: 'background.paper' } } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(v) => {
                if (v) setEndDate(v);
              }}
              format="dd/MM/yyyy"
              slotProps={{ textField: { size: 'small', sx: { width: 160, bgcolor: 'background.paper' } } }}
            />
          </LocalizationProvider>
          <Button
            variant="outlined"
            onClick={fetchSalaryReportData}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            sx={styles.exportBtn}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportExcel}
            startIcon={<TableChart />}
            sx={styles.exportBtn}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            onClick={handleExportPDF}
            startIcon={<PictureAsPdf />}
            sx={{ bgcolor: THEME.primary, borderRadius: '8px', padding: '8px 16px', '&:hover': { bgcolor: '#334155' } }}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Tabs matching 4 Sheets */}
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        textColor="primary"
        indicatorColor="primary"
        sx={{ borderBottom: `1px solid ${THEME.border}`, mb: 1 }}
      >
        <Tab label={`${selectedMonthLabel} - MARABES Group`} />
        <Tab label={`${selectedMonthLabel} - MARABES Consulting`} />
        <Tab label={`Team ${selectedMonthLabel}`} />
        <Tab label="Offboarding" />
      </Tabs>

      {/* Editable Switcher */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1} gap={1}>
        <Tooltip title="Double-click inputs or check this option to adjust values manually">
          <IconButton size="small" onClick={() => setIsEditing(!isEditing)} color={isEditing ? 'success' : 'default'}>
            <Edit sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Typography variant="body2" sx={{ color: THEME.secondary, fontWeight: 500 }}>
          {isEditing ? 'Live Editing Active (Auto-Formula Updates)' : 'View Only Mode'}
        </Typography>
      </Box>

      {/* Sheet Contents */}
      <TableContainer component={Paper} sx={styles.tableCard}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={12}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            {activeTab === 0 && (
              <Box>
                {/* Section Nador */}
                <Box p={3} sx={{ backgroundColor: '#F8FAFC', borderBottom: `1px solid ${THEME.border}` }}>
                  <Typography variant="subtitle1" fontWeight={700} color={THEME.sageDark}>
                    Report {selectedMonthLabel} - MARABES Group (Nador)
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={styles.excelTh} colSpan={3}>Employee Details</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: THEME.deductLight }} colSpan={4}>Deductions (Hours)</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: THEME.addLight }} colSpan={3}>Additions (Hours)</TableCell>
                      <TableCell sx={styles.excelTh}>Links</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={styles.th}>First Name</TableCell>
                      <TableCell sx={styles.th}>Last Name</TableCell>
                      <TableCell sx={styles.th}>Start Date</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Commitments</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Sickness</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Unpaid</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: '#EAEEF3', fontWeight: 'bold' }}>Total Deduct</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.addLight }}>Extra Week</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.addLight }}>Extra Weekend</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: '#EAEEF3', fontWeight: 'bold' }}>Hours Paid</TableCell>
                      <TableCell sx={styles.th}>Links</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nadorData.map((row) => {
                      const totalDeduct = row.commitments + row.sickness + row.unpaid;
                      const hoursPaid = (row.extraWeek + row.extraWeekend) - totalDeduct;
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ ...styles.td, fontWeight: 600 }}>{row.firstName}</TableCell>
                          <TableCell sx={styles.td}>{row.lastName}</TableCell>
                          <TableCell sx={styles.td}>{row.startDate}</TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.commitments}
                                onChange={(e) => handleNumericChange('nador', row.id, 'commitments', e.target.value)}
                              />
                            ) : (
                              row.commitments
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.sickness}
                                onChange={(e) => handleNumericChange('nador', row.id, 'sickness', e.target.value)}
                              />
                            ) : (
                              row.sickness
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.unpaid}
                                onChange={(e) => handleNumericChange('nador', row.id, 'unpaid', e.target.value)}
                              />
                            ) : (
                              row.unpaid
                            )}
                          </TableCell>
                          <TableCell sx={{ ...styles.td, fontWeight: 'bold', bgcolor: '#F8FAFC' }}>{totalDeduct}</TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.extraWeek}
                                onChange={(e) => handleNumericChange('nador', row.id, 'extraWeek', e.target.value)}
                              />
                            ) : (
                              row.extraWeek
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.extraWeekend}
                                onChange={(e) => handleNumericChange('nador', row.id, 'extraWeekend', e.target.value)}
                              />
                            ) : (
                              row.extraWeekend
                            )}
                          </TableCell>
                          <TableCell sx={{ ...styles.td, fontWeight: 'bold', color: hoursPaid < 0 ? 'red' : 'green', bgcolor: '#F8FAFC' }}>
                            {hoursPaid}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {(() => {
                              const linksArray = row.links && row.links !== 'N/A' ? row.links.split(',').map(l => l.trim()).filter(Boolean) : [];
                              if (linksArray.length === 0) return '-';
                              return (
                                <Box display="flex" gap={0.5} justifyContent="center">
                                  {linksArray.map((url, idx) => (
                                    <Button
                                      key={idx}
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Visibility sx={{ fontSize: '10px !important' }} />}
                                      onClick={() => setPreviewLink(url)}
                                      sx={{
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        padding: '2px 6px',
                                        minWidth: 'auto',
                                        borderRadius: '4px',
                                        borderColor: THEME.accent,
                                        color: THEME.accent,
                                        '&:hover': {
                                          backgroundColor: '#EFF6FF',
                                          borderColor: THEME.accent
                                        }
                                      }}
                                    >
                                      {linksArray.length > 1 ? `View ${idx + 1}` : 'View'}
                                    </Button>
                                  ))}
                                </Box>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Section Tangier */}
                <Box p={3} mt={4} sx={{ backgroundColor: '#F8FAFC', borderBottom: `1px solid ${THEME.border}`, borderTop: `1px solid ${THEME.border}` }}>
                  <Typography variant="subtitle1" fontWeight={700} color={THEME.sageDark}>
                    Report {selectedMonthLabel} - MARABES Group (Tangier)
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={styles.excelTh} colSpan={3}>Employee Details</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: THEME.deductLight }} colSpan={4}>Deductions (Hours)</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: THEME.addLight }} colSpan={3}>Additions (Hours)</TableCell>
                      <TableCell sx={styles.excelTh}>Links</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={styles.th}>First Name</TableCell>
                      <TableCell sx={styles.th}>Last Name</TableCell>
                      <TableCell sx={styles.th}>Start Date</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Commitments</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Sickness</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Unpaid</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: '#EAEEF3', fontWeight: 'bold' }}>Total Deduct</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.addLight }}>Extra Week</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.addLight }}>Extra Weekend</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: '#EAEEF3', fontWeight: 'bold' }}>Hours Paid</TableCell>
                      <TableCell sx={styles.th}>Links</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tangierData.map((row) => {
                      const totalDeduct = row.commitments + row.sickness + row.unpaid;
                      const hoursPaid = (row.extraWeek + row.extraWeekend) - totalDeduct;
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ ...styles.td, fontWeight: 600 }}>{row.firstName}</TableCell>
                          <TableCell sx={styles.td}>{row.lastName}</TableCell>
                          <TableCell sx={styles.td}>{row.startDate}</TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.commitments}
                                onChange={(e) => handleNumericChange('tangier', row.id, 'commitments', e.target.value)}
                              />
                            ) : (
                              row.commitments
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.sickness}
                                onChange={(e) => handleNumericChange('tangier', row.id, 'sickness', e.target.value)}
                              />
                            ) : (
                              row.sickness
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.unpaid}
                                onChange={(e) => handleNumericChange('tangier', row.id, 'unpaid', e.target.value)}
                              />
                            ) : (
                              row.unpaid
                            )}
                          </TableCell>
                          <TableCell sx={{ ...styles.td, fontWeight: 'bold', bgcolor: '#F8FAFC' }}>{totalDeduct}</TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.extraWeek}
                                onChange={(e) => handleNumericChange('tangier', row.id, 'extraWeek', e.target.value)}
                              />
                            ) : (
                              row.extraWeek
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.extraWeekend}
                                onChange={(e) => handleNumericChange('tangier', row.id, 'extraWeekend', e.target.value)}
                              />
                            ) : (
                              row.extraWeekend
                            )}
                          </TableCell>
                          <TableCell sx={{ ...styles.td, fontWeight: 'bold', color: hoursPaid < 0 ? 'red' : 'green', bgcolor: '#F8FAFC' }}>
                            {hoursPaid}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {(() => {
                              const linksArray = row.links && row.links !== 'N/A' ? row.links.split(',').map(l => l.trim()).filter(Boolean) : [];
                              if (linksArray.length === 0) return '-';
                              return (
                                <Box display="flex" gap={0.5} justifyContent="center">
                                  {linksArray.map((url, idx) => (
                                    <Button
                                      key={idx}
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Visibility sx={{ fontSize: '10px !important' }} />}
                                      onClick={() => setPreviewLink(url)}
                                      sx={{
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        padding: '2px 6px',
                                        minWidth: 'auto',
                                        borderRadius: '4px',
                                        borderColor: THEME.accent,
                                        color: THEME.accent,
                                        '&:hover': {
                                          backgroundColor: '#EFF6FF',
                                          borderColor: THEME.accent
                                        }
                                      }}
                                    >
                                      {linksArray.length > 1 ? `View ${idx + 1}` : 'View'}
                                    </Button>
                                  ))}
                                </Box>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Box p={3} sx={{ backgroundColor: '#F8FAFC', borderBottom: `1px solid ${THEME.border}` }}>
                  <Typography variant="subtitle1" fontWeight={700} color={THEME.sageDark}>
                    Report {selectedMonthLabel} - MARABES Consulting
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={styles.excelTh} colSpan={3}>Employee Details</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: THEME.deductLight }} colSpan={4}>Deductions (Hours)</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: THEME.addLight }} colSpan={3}>Additions (Hours)</TableCell>
                      <TableCell sx={styles.excelTh}>Links</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={styles.th}>First Name</TableCell>
                      <TableCell sx={styles.th}>Last Name</TableCell>
                      <TableCell sx={styles.th}>Start Date</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Commitments</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Sickness</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Unpaid</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: '#EAEEF3', fontWeight: 'bold' }}>Total Deduct</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.addLight }}>Extra Week</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.addLight }}>Extra Weekend</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: '#EAEEF3', fontWeight: 'bold' }}>Hours Paid</TableCell>
                      <TableCell sx={styles.th}>Links</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consultingData.map((row) => {
                      const totalDeduct = row.commitments + row.sickness + row.unpaid;
                      const hoursPaid = (row.extraWeek + row.extraWeekend) - totalDeduct;
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ ...styles.td, fontWeight: 600 }}>{row.firstName}</TableCell>
                          <TableCell sx={styles.td}>{row.lastName}</TableCell>
                          <TableCell sx={styles.td}>{row.startDate}</TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.commitments}
                                onChange={(e) => handleNumericChange('consulting', row.id, 'commitments', e.target.value)}
                              />
                            ) : (
                              row.commitments
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.sickness}
                                onChange={(e) => handleNumericChange('consulting', row.id, 'sickness', e.target.value)}
                              />
                            ) : (
                              row.sickness
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.unpaid}
                                onChange={(e) => handleNumericChange('consulting', row.id, 'unpaid', e.target.value)}
                              />
                            ) : (
                              row.unpaid
                            )}
                          </TableCell>
                          <TableCell sx={{ ...styles.td, fontWeight: 'bold', bgcolor: '#F8FAFC' }}>{totalDeduct}</TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.extraWeek}
                                onChange={(e) => handleNumericChange('consulting', row.id, 'extraWeek', e.target.value)}
                              />
                            ) : (
                              row.extraWeek
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.extraWeekend}
                                onChange={(e) => handleNumericChange('consulting', row.id, 'extraWeekend', e.target.value)}
                              />
                            ) : (
                              row.extraWeekend
                            )}
                          </TableCell>
                          <TableCell sx={{ ...styles.td, fontWeight: 'bold', color: hoursPaid < 0 ? 'red' : 'green', bgcolor: '#F8FAFC' }}>
                            {hoursPaid}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {(() => {
                              const linksArray = row.links && row.links !== 'N/A' ? row.links.split(',').map(l => l.trim()).filter(Boolean) : [];
                              if (linksArray.length === 0) return '-';
                              return (
                                <Box display="flex" gap={0.5} justifyContent="center">
                                  {linksArray.map((url, idx) => (
                                    <Button
                                      key={idx}
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Visibility sx={{ fontSize: '10px !important' }} />}
                                      onClick={() => setPreviewLink(url)}
                                      sx={{
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        padding: '2px 6px',
                                        minWidth: 'auto',
                                        borderRadius: '4px',
                                        borderColor: THEME.accent,
                                        color: THEME.accent,
                                        '&:hover': {
                                          backgroundColor: '#EFF6FF',
                                          borderColor: THEME.accent
                                        }
                                      }}
                                    >
                                      {linksArray.length > 1 ? `View ${idx + 1}` : 'View'}
                                    </Button>
                                  ))}
                                </Box>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Box p={3} sx={{ backgroundColor: '#F8FAFC', borderBottom: `1px solid ${THEME.border}` }}>
                  <Typography variant="subtitle1" fontWeight={700} color={THEME.sageDark}>
                    Team {selectedMonthLabel}
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={styles.excelTh} colSpan={3}>Employee Details</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: THEME.deductLight }} colSpan={4}>Deductions (Hours)</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: THEME.addLight }}>Additions</TableCell>
                      <TableCell sx={styles.excelTh} colSpan={2}>Calculation & Note</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={styles.th}>First Name</TableCell>
                      <TableCell sx={styles.th}>Last Name</TableCell>
                      <TableCell sx={styles.th}>Start Date</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Commitments</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Sickness</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.deductLight }}>Unpaid</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: '#EAEEF3', fontWeight: 'bold' }}>Total Deduct</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: THEME.addLight }}>Extra Worked</TableCell>
                      <TableCell sx={{ ...styles.th, bgcolor: '#EAEEF3', fontWeight: 'bold' }}>Hours Paid</TableCell>
                      <TableCell sx={styles.th}>New Arrival Feedback</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamData.map((row) => {
                      const totalDeduct = row.commitments + row.sickness + row.unpaid;
                      const hoursPaid = row.extraWorked - totalDeduct;
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ ...styles.td, fontWeight: 600 }}>{row.firstName}</TableCell>
                          <TableCell sx={styles.td}>{row.lastName}</TableCell>
                          <TableCell sx={styles.td}>{row.startDate}</TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.commitments}
                                onChange={(e) => handleNumericChange('team', row.id, 'commitments', e.target.value)}
                              />
                            ) : (
                              row.commitments
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.sickness}
                                onChange={(e) => handleNumericChange('team', row.id, 'sickness', e.target.value)}
                              />
                            ) : (
                              row.sickness
                            )}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.unpaid}
                                onChange={(e) => handleNumericChange('team', row.id, 'unpaid', e.target.value)}
                              />
                            ) : (
                              row.unpaid
                            )}
                          </TableCell>
                          <TableCell sx={{ ...styles.td, fontWeight: 'bold', bgcolor: '#F8FAFC' }}>{totalDeduct}</TableCell>
                          <TableCell sx={styles.td}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={styles.editableInput}
                                value={row.extraWorked}
                                onChange={(e) => handleNumericChange('team', row.id, 'extraWorked', e.target.value)}
                              />
                            ) : (
                              row.extraWorked
                            )}
                          </TableCell>
                          <TableCell sx={{ ...styles.td, fontWeight: 'bold', color: hoursPaid < 0 ? 'red' : 'green', bgcolor: '#F8FAFC' }}>
                            {hoursPaid}
                          </TableCell>
                          <TableCell sx={styles.td}>
                            {row.comment ? (
                              <Tooltip title={row.comment}>
                                <Chip 
                                  label={row.comment.length > 15 ? row.comment.substring(0, 15) + '...' : row.comment} 
                                  color={row.commentType === 'POSITIVE' ? 'success' : 'error'} 
                                  size="small" 
                                  variant="outlined" 
                                />
                              </Tooltip>
                            ) : ''}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Box p={3} sx={{ backgroundColor: '#FEE2E2', borderBottom: '1px solid #FCA5A5' }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#991B1B">
                    OFFBOARDING LOGS
                  </Typography>
                </Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: '#FEE2E2', color: '#991B1B' }}>First Name</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Last Name</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Entry Date</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Exit Date</TableCell>
                      <TableCell sx={{ ...styles.excelTh, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Salary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offboardingData.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ ...styles.td, fontWeight: 600 }}>{row.firstName}</TableCell>
                        <TableCell sx={styles.td}>{row.lastName}</TableCell>
                        <TableCell sx={styles.td}>{row.entryDate}</TableCell>
                        <TableCell sx={{ ...styles.td, color: '#DC2626', fontWeight: 600 }}>{row.exitDate}</TableCell>
                        <TableCell sx={{ ...styles.td, fontWeight: 700, bgcolor: '#FEF2F2' }}>{row.salary}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </TableContainer>

      {/* Premium Overlay Component for Justifications */}
      <Dialog
        open={Boolean(previewLink)}
        onClose={() => setPreviewLink(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)'
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: THEME.primary, color: '#white' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#white' }}>
            Justification Document
          </Typography>
          <IconButton onClick={() => setPreviewLink(null)} sx={{ color: '#94A3B8', '&:hover': { color: '#white' } }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '600px', display: 'flex', flexDirection: 'column', bgcolor: '#F8FAFC' }}>
          {previewLink && (
            <iframe
              src={previewLink}
              title="Document Preview"
              width="100%"
              height="100%"
              style={{ border: 'none', flexGrow: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between', bgcolor: '#F1F5F9' }}>
          <Typography variant="caption" color="textSecondary">
            If the preview doesn't load, click "Open in New Tab" below.
          </Typography>
          <Box display="flex" gap={1.5}>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              href={previewLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                borderColor: THEME.border,
                color: THEME.secondary,
                '&:hover': { borderColor: THEME.accent, color: THEME.accent }
              }}
            >
              Open in New Tab
            </Button>
            <Button
              variant="contained"
              onClick={() => setPreviewLink(null)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                bgcolor: THEME.primary,
                '&:hover': { bgcolor: '#334155' }
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalaryReport;
